import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'
import { userManager } from '@/auth/userManager'
import { AuthContext, type AuthStatus } from '@/auth/AuthContext'
import { fetchUserInfo, type UserInfo } from '@/api/auth'
import { applyLanguageHint, currentLanguage } from '@/i18n'
import type { Role } from '@/auth/roles'

const ROLE_PREFIX = 'ROLE_'

/**
 * A stored session (page reload, second tab) is confirmed against the backend before anything
 * authenticated renders — otherwise a protected view flashes up before a stale token is caught.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>('loading')
  const [user, setUser] = useState<UserInfo | null>(null)

  /**
   * Must NOT call `userManager.removeUser()`: removeUser fires `userUnloaded` unconditionally and
   * this is the handler for that event, so calling it here recurses without end and pegs the main
   * thread. That froze the page on logout, since signoutRedirect calls removeUser internally.
   */
  const resetSession = useCallback(() => {
    setUser(null)
    // A logout in flight owns the status until its redirect happens.
    setStatus((current) => (current === 'signingOut' ? current : 'anonymous'))
  }, [])

  const clearSession = useCallback(async () => {
    await userManager.removeUser()
    resetSession()
  }, [resetSession])

  const loadUser = useCallback(async () => {
    const oidcUser = await userManager.getUser()
    if (!oidcUser || oidcUser.expired) {
      setUser(null)
      setStatus('anonymous')
      return
    }
    // The `locale` the user set in their Keycloak profile, if any. Only a hint — a choice made
    // in this app wins, see applyLanguageHint.
    applyLanguageHint(oidcUser.profile.locale)
    try {
      setUser(await fetchUserInfo())
      setStatus('authenticated')
    } catch {
      await clearSession()
    }
  }, [clearSession])

  useEffect(() => {
    // loadUser awaits before it touches state, which the rule cannot see.
    // oxlint-disable-next-line react/set-state-in-effect
    void loadUser()

    // A failed silent renew, or a logout in another tab, must not leave a stale UI behind.
    const onExpired = () => void clearSession()
    // Only reset state here — see resetSession on why this must not remove the user again.
    const onUnloaded = () => resetSession()
    userManager.events.addSilentRenewError(onExpired)
    userManager.events.addUserUnloaded(onUnloaded)
    return () => {
      userManager.events.removeSilentRenewError(onExpired)
      userManager.events.removeUserUnloaded(onUnloaded)
    }
  }, [loadUser, clearSession, resetSession])

  const strip = (role: string) => (role.startsWith(ROLE_PREFIX) ? role.slice(ROLE_PREFIX.length) : role)

  const roles = useMemo(() => (user?.roles ?? []).map(strip), [user])
  const effectiveRoles = useMemo(() => (user?.effectiveRoles ?? []).map(strip), [user])

  const value = useMemo(
    () => ({
      status,
      user,
      roles,
      effectiveRoles,
      hasRole: (role: Role) => effectiveRoles.includes(role),
      login: async () => {
        // Come back to wherever the user was heading, not always to the start page.
        await userManager.signinRedirect({
          state: { returnTo: window.location.pathname + window.location.search },
          // Without this the Keycloak login screen picks its own language and the user changes
          // language halfway through signing in.
          extraQueryParams: { ui_locales: currentLanguage() },
        })
      },
      logout: async () => {
        // Not 'anonymous': that would send RequireAuth to /login, whose effect starts a
        // signinRedirect racing the signoutRedirect below.
        setStatus('signingOut')
        // Ends the Keycloak SSO session too; removeUser alone would let the next login walk
        // straight back in without a prompt.
        await userManager.signoutRedirect()
      },
    }),
    [status, user, roles, effectiveRoles],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
